import templateUrl from './onboarding-update-modal.component.html';

// APEX-977 — employee-facing profile-level Update modal.
//
// Two stages in one component:
//   1) 'select'  — the employee picks WHICH group of forms to re-open
//                  (Personal Information / Tax Forms / Direct Deposit / All Forms).
//   2) 'confirm' — secondary confirmation gate. Confirm finalizes; Cancel/X aborts
//                  with zero changes (this IS the "Cancel Update" path — no unlock happens
//                  until the employee confirms here, so nothing needs to be restored).
//
// Resolves with { option, formIds } where formIds is the list of backend FormIDs to unlock
// (see UpdateChangeFormMapping switch: 6=UserProfile, 5=FederalW4, -2=W9, 2=ProvinceW4,
// 3=I9, 7=DirectDeposit). Options adapt for Dept 900 / 1099 employees (FW9 instead of the
// W2 tax forms) based on the injected `is1099` flag.
export const OnboardingUpdateModalComponent = {
    templateUrl,
    controller: OnboardingUpdateModalController,
    controllerAs: 'vm',
    bindings: {
        resolve: '<',
        close: '&',
        dismiss: '&'
    }
};

// Option key -> backend FormIDs. Computed per employee type.
//   W2:   personal = Profile + FederalW4 + StateW4 + I9; tax = FederalW4 + StateW4
//   1099: personal = Profile + W9;                       tax = W9
// DD and All Forms differ only by the tax-form subset.
function buildOptions(is1099, paper) {
    // Paper apps may re-open Direct Deposit only; every other group is shown but disabled.
    const dis = (value) => !!paper && value !== 'dd';
    if (is1099) {
        return [
            { value: 'personal-information', label: 'Personal Information', note: 'Profile, Federal W-9', formIds: [6, -2], disabled: dis('personal-information') },
            { value: 'tax-forms', label: 'Tax Forms', note: 'Federal W-9', formIds: [-2], disabled: dis('tax-forms') },
            { value: 'dd', label: 'Direct Deposit', note: 'Direct Deposit only', formIds: [7], disabled: dis('dd') },
            { value: 'all-forms', label: 'All Forms', note: 'Profile, Federal W-9, Direct Deposit', formIds: [6, -2, 7], disabled: dis('all-forms') }
        ];
    }
    return [
        { value: 'personal-information', label: 'Personal Information', note: 'Profile, Federal W-4, State W-4, I-9', formIds: [6, 5, 2, 3], disabled: dis('personal-information') },
        { value: 'tax-forms', label: 'Tax Forms', note: 'Federal W-4, State W-4', formIds: [5, 2], disabled: dis('tax-forms') },
        { value: 'dd', label: 'Direct Deposit', note: 'Direct Deposit only', formIds: [7], disabled: dis('dd') },
        { value: 'all-forms', label: 'All Forms', note: 'Profile, Federal W-4, State W-4, I-9, Direct Deposit', formIds: [6, 5, 2, 3, 7], disabled: dis('all-forms') }
    ];
}

function OnboardingUpdateModalController() {
    'ngInject';
    const vm = this;
    vm.next = next;
    vm.back = back;
    vm.confirm = confirm;
    vm.cancel = cancel;
    vm.canContinue = canContinue;
    vm.isDdSelected = isDdSelected;

    vm.$onInit = function () {
        vm.is1099 = !!(vm.resolve && vm.resolve.is1099);
        vm.paper = !!(vm.resolve && vm.resolve.paper);
        vm.options = buildOptions(vm.is1099, vm.paper);
        // Paper apps can only update Direct Deposit -> pre-select it.
        vm.choice = vm.paper ? 'dd' : null; // selected option value
        vm.stage = 'select'; // 'select' | 'confirm'
    };

    function canContinue() {
        const opt = vm.options.find((o) => o.value === vm.choice);
        return !!opt && !opt.disabled;
    }

    // True when the chosen group re-opens Direct Deposit (DD-only or All) -> show the DD-clear warning.
    function isDdSelected() {
        const opt = vm.options.find((o) => o.value === vm.choice);
        return !!opt && opt.formIds.indexOf(7) !== -1;
    }

    // Stage 1 -> Stage 2 (secondary confirmation). No mutation yet.
    function next() {
        if (!canContinue()) {
            return;
        }
        vm.stage = 'confirm';
    }

    // Stage 2 -> back to selection (lets the employee change their pick without aborting).
    function back() {
        vm.stage = 'select';
    }

    // Finalize: resolve with the chosen option + its FormIDs. Caller performs the unlock.
    function confirm() {
        const opt = vm.options.find((o) => o.value === vm.choice);
        if (!opt || opt.disabled) {
            return;
        }
        vm.close({
            $value: { option: opt.value, formIds: opt.formIds }
        });
    }

    // Cancel / X at EITHER stage aborts with zero changes.
    function cancel() {
        vm.dismiss({ $value: 'cancel' });
    }
}

// uib-modal descriptor (mirrors FormChoiceModal styling/behavior).
export const OnboardingUpdateModal = {
    component: 'apexOnboardingUpdateModal',
    windowClass: 'xx-dialog pagespinner',
    size: 'md',
    backdrop: 'static',
    keyboard: false
};
